import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './client';

type DynamicResult<Row> = { data: Row[] | null; error: PostgrestError | null };
type DynamicSingleResult<Row> = { data: Row | null; error: PostgrestError | null };

/**
 * Filter/transform stage of a dynamic query — awaitable, and chainable with the
 * PostgREST methods our dynamic-table helpers actually use. Returned by every
 * verb on {@link DynamicQuery}. Add methods here as new call sites need them.
 */
export interface DynamicFilter<Row = Record<string, unknown>>
  extends PromiseLike<DynamicResult<Row>> {
  select(columns?: string): DynamicFilter<Row>;
  or(filter: string): DynamicFilter<Row>;
  eq(column: string, value: unknown): DynamicFilter<Row>;
  order(column: string, options?: { ascending?: boolean }): DynamicFilter<Row>;
  limit(count: number): DynamicFilter<Row>;
  /** Resolve to a single row (PostgREST `.single()`). */
  single(): PromiseLike<DynamicSingleResult<Row>>;
  /** Resolve to one row or `null` when none matches — no error for zero rows (PostgREST `.maybeSingle()`). */
  maybeSingle(): PromiseLike<DynamicSingleResult<Row>>;
}

/** Entry stage returned by {@link dynamicTable}: pick a verb, then chain filters. */
export interface DynamicQuery<Row = Record<string, unknown>> {
  select(columns?: string): DynamicFilter<Row>;
  insert(values: Record<string, unknown>): DynamicFilter<Row>;
  update(values: Record<string, unknown>): DynamicFilter<Row>;
  delete(): DynamicFilter<Row>;
}

/**
 * Untyped PostgREST query builder for a table addressed by a runtime `string`,
 * narrowed to the minimal surface our dynamic-table helpers use and generic over
 * the row shape (`Row`, default `Record<string, unknown>`).
 *
 * ## Why this exists
 * Supabase's generated client only accepts *literal* table names. Passing a
 * dynamic `string` to `supabase.from(...)` makes the overload resolver fall
 * through and emit `TS2589` ("Type instantiation is excessively deep") plus
 * `never`-typed columns. Worse, a dynamic *select* string makes PostgREST run
 * expensive query-string parsing that exhausts TypeScript's per-file
 * instantiation budget — silently degrading *other*, literal-table queries in
 * the same module to `never`.
 *
 * Casting through these minimal interfaces (which carry **no** PostgREST
 * generics) sidesteps both problems with zero per-call casts and no `any`.
 * Runtime behaviour is identical to the typed client.
 *
 * ## When to use
 * Only for genuinely dynamic table access — helpers that operate on any
 * `*_types` / taxonomy table by name (see `useDualScopeTypes`, `useEntityOptions`,
 * docs/DUAL_SCOPE_TYPES.md). Prefer the typed `supabase.from('literal')`
 * everywhere else; you forgo compile-time column checking here, which is
 * unavoidable when the table isn't known until runtime.
 *
 * @example
 * const { data } = await dynamicTable<AssetType>('asset_types')
 *   .select('*')
 *   .eq('organization_id', orgId);
 */
export function dynamicTable<Row = Record<string, unknown>>(
  name: string,
): DynamicQuery<Row> {
  return (supabase as unknown as SupabaseClient).from(name) as unknown as DynamicQuery<Row>;
}